import Smartlook from 'smartlook-client';

export const PROJECT_SWITCHED = 'project_switched';

let currentSessionId: string | null = null;

function generateSessionId(userId: string | number): string {
  return `${userId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

let currentUniqueEmail: string | null = null;

function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

function generateContextEmail(
  email: string,
  tenant?: string | null,
  project?: string | null
): string {
  const contextKey = [tenant, project].filter(Boolean).join('_');
  if (!contextKey) return email;
  const [local, domain] = email.split('@');
  return `${local}_${simpleHash(contextKey)}@${domain}`;
}

function identifyWith(
  sessionId: string,
  user: {
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    cognitoUsername?: string | null;
  },
  options?: { tenant?: string | null; project?: string | null },
  newSession = false
) {
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.cognitoUsername;

  if (newSession && user.email) {
    currentUniqueEmail = generateContextEmail(user.email, options?.tenant, options?.project);
  }

  Smartlook.identify(sessionId, {
    ...(name ? { name } : {}),
    ...(currentUniqueEmail ? { email: currentUniqueEmail } : {}),
    ...(user.email ? { userEmail: user.email } : {}),
    ...(options?.tenant ? { tenant: options.tenant } : {}),
    ...(options?.project ? { project: options.project } : {}),
  });
}

/** Creates a new unique visitor ID and identifies. Use on first load and context switches. */
export function smartlookIdentifyUser(
  user: {
    id?: number | string | null;
    firstName?: string | null;
    lastName?: string | null;
    cognitoUsername?: string | null;
    email?: string | null;
  },
  options?: { tenant?: string | null; project?: string | null }
) {
  if (!user.id || !Smartlook.initialized()) return;

  currentSessionId = generateSessionId(user.id);
  identifyWith(currentSessionId, user, options, true);
}

/** Re-applies the current identity without generating a new ID. Use on tab visibility restore. */
export function smartlookReapplyIdentity(
  user: {
    id?: number | string | null;
    firstName?: string | null;
    lastName?: string | null;
    cognitoUsername?: string | null;
    email?: string | null;
  },
  options?: { tenant?: string | null; project?: string | null }
) {
  if (!user.id || !Smartlook.initialized() || !currentSessionId) return;

  identifyWith(currentSessionId, user, options);
}

/** Clears visitor cookies and creates a fresh visitor. Call on logout. */
export function smartlookAnonymize() {
  if (!Smartlook.initialized()) return;
  currentSessionId = null;
  currentUniqueEmail = null;
  Smartlook.anonymize();
}

export function smartlookTrack(
  eventName: string,
  properties?: Record<string, string | number | boolean>
) {
  if (!Smartlook.initialized()) return;
  Smartlook.track(eventName, properties ?? {});
}
